<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-overlay :value="isLoading" z-index="999" :absolute="embedded">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded" class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Edit Vendor</span>
        </v-card-title>
        <v-card-text>
          <!-- Basic Information -->
          <AppSectionHeader label="Basic Information" />
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.name"
                outlined
                :error-messages="errors.name"
              >
                <template v-slot:label>Vendor Name <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.contact_person"
                outlined
                :error-messages="errors.contact_person"
              >
                <template v-slot:label>Contact Person <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.phone"
                outlined
                hint="10-digit mobile number"
                persistent-hint
                :error-messages="errors.phone"
              >
                <template v-slot:label>Phone <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.email"
                outlined
                hint="Valid email address"
                persistent-hint
                :error-messages="errors.email"
              >
                <template v-slot:label>Email <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Address -->
          <AppSectionHeader label="Address" />
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.address"
                outlined
                rows="2"
                auto-grow
                :error-messages="errors.address"
              >
                <template v-slot:label>Full Address <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-textarea>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.city"
                outlined
                :error-messages="errors.city"
              >
                <template v-slot:label>City <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.state"
                outlined
                :error-messages="errors.state"
              >
                <template v-slot:label>State <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.pin_code"
                outlined
                :error-messages="errors.pin_code"
              >
                <template v-slot:label>Pincode <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Tax Information -->
          <AppSectionHeader label="Tax Information" />
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.gstin"
                outlined
                label="GST Number"
                hint="Format: 2 digits + 10-char PAN + 1 digit + Z + 1 char"
                persistent-hint
                @input="form.gstin = form.gstin ? form.gstin.toUpperCase() : form.gstin"
                :error-messages="errors.gstin"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.pan_number"
                outlined
                label="PAN Number"
                hint="Format: 5 letters + 4 digits + 1 letter (all uppercase)"
                persistent-hint
                @input="form.pan_number = form.pan_number ? form.pan_number.toUpperCase() : form.pan_number"
                :error-messages="errors.pan_number"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Bank Details -->
          <AppSectionHeader label="Bank Details" />
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_name"
                outlined
                label="Bank Name"
                :error-messages="errors.bank_name"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.branch_name"
                outlined
                label="Branch Name"
                :error-messages="errors.branch_name"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_account_number"
                outlined
                label="Account Number"
                hint="9 to 18 digit bank account number"
                persistent-hint
                :error-messages="errors.bank_account_number"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_ifsc"
                outlined
                label="IFSC Code"
                hint="Format: 4 letters + 0 + 6 alphanumeric characters"
                persistent-hint
                @input="form.bank_ifsc = form.bank_ifsc ? form.bank_ifsc.toUpperCase() : form.bank_ifsc"
                :error-messages="errors.bank_ifsc"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_account_name"
                outlined
                label="Account Holder Name"
                :error-messages="errors.bank_account_name"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Status -->
          <AppSectionHeader label="Status" />
          <v-row align="center">
            <v-col sm="8">
              <span class="text-caption grey--text">Set vendor status</span>
            </v-col>
            <v-col sm="4" class="text-right">
              <v-switch
                class="mt-0 d-inline-flex"
                v-model="form.is_active"
                inset
                :label="form.is_active ? 'Active' : 'Inactive'"
              ></v-switch>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <div class="text-right">
            <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
            <v-btn
              :loading="isLoading"
              :disabled="isLoading"
              color="primary"
              rounded
              dark
              @click="save"
            >
              Update
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </component>
  </component>
</template>
<script>
import axios from "axios";
import AppSectionHeader from "@/components/AppSectionHeader.vue";
export default {
  name: "VendorEditPage",
  components: {
    AppSectionHeader,
  },
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Vendors list's inline "Edit Vendor") instead of its own routed
    // page - the id comes from the `vendorId` prop rather than the route,
    // and it emits `saved`/`close` rather than navigating away.
    embedded: { type: Boolean, default: false },
    vendorId: { type: String, default: null },
  },
  data() {
    return {
      form: {},
      isLoading: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      let encryptedId = this.vendorId || this.$route.params.id;
      await axios
        .get(`/vendors/${encryptedId}`)
        .then((response) => {
          this.form = response.data.data;
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});
        const encryptedId = this.vendorId || this.$route.params.id;
        await axios.patch(`/vendors/${encryptedId}`, this.form);
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("saved");
          this.$emit("close");
        } else {
          this.$router.push("/vendors");
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
      }
    },
  },
};
</script>
